'use client';
import {KRow} from '@/components/layout/KRow';
import {
  KIGetMessagesParams,
  NotificationMessage,
  getNotificationsDefaultFetchParams,
  useGetNotifications,
} from '@/hooks/useGetNotifications';
import colors from '@/styles/colors';
import {Box} from '@mui/material';
import {useEffect, useState} from 'react';
import {NotificationArchive} from './NotificationArchive';
import {NotificationCard} from './NotificationCard';
import {NotificationFilters} from './NotificationFilters';
import {NotificationReadUnread} from './NotificationReadUnread';
import {NotificationTags} from './NotificationTags';
import {NotificationsList} from './NotificationsList';

/**
 * Notification Center with left nav using NotificationsList and
 * selected notification view using NotificationView.
 * Defaults selected to first notification.
 *
 * Clicking in list selects notification to view.
 * Checks route for notification id to use as default selected.
 *
 */
export const NotificationCenterView = ({navToMessageID}: {navToMessageID?: string}) => {
  const [filters, setFilters] = useState<KIGetMessagesParams>({...getNotificationsDefaultFetchParams, isRead: false});
  const {messages, inbox, onChangeMessage} = useGetNotifications(filters);
  const [selected, setSelected] = useState<NotificationMessage | undefined>();

  const onClickNotification = (message: NotificationMessage) => {
    setSelected(message);

    if (!message.read) {
      inbox.markMessageRead(message.id);
      message.read = true;
      onChangeMessage(message);
    }
  };

  useEffect(() => {
    const message = messages?.find((msg) => msg.id === navToMessageID);

    /**
     * If no message selected then select router message else first message read.
     * If selected message then update selected message.
     */
    if (message && !selected) {
      setSelected(message);
    } else if (!selected && messages.length > 0) {
      setSelected(messages[0]);
    } else if (selected) {
      const update = messages?.find((msg) => msg.id === selected?.id);
      setSelected(update);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, navToMessageID]);

  const onChange = (message: NotificationMessage) => {
    // if archived and selected then move selected to next item in list or end of list
    if (message.archived && message.id === selected?.id) {
      const index = messages?.findIndex((msg) => msg.id === message.id);
      if (index !== -1) {
        let selecting = undefined;
        if (messages.length > index + 1) {
          selecting = messages[index + 1];
        } else if (index - 1 >= 0) {
          selecting = messages[index - 1];
        }

        setSelected(selecting);
      }
    }

    onChangeMessage(message);
  };

  const onChangeFilters = (params: KIGetMessagesParams) => {
    setFilters({...getNotificationsDefaultFetchParams, ...params});
  };

  return (
    <KRow
      data-testid={'NotificationCenter-container'}
      sx={{height: '100%', justifyContent: 'self', alignItems: 'start'}}
    >
      <Box
        sx={{
          padding: '20px 10px',
          width: 220,
          minHeight: '100%',
          maxHeight: '100%',
          overflow: 'auto',
          backgroundColor: colors.calcite,
          borderRight: `1px solid ${colors.veryLightBlue}`,
        }}
      >
        <NotificationFilters selected={filters} onChangeFilter={onChangeFilters} />
      </Box>

      <Box
        sx={{
          width: 350,
          minHeight: '100%',
          maxHeight: '100%',
          overflow: 'auto',
          backgroundColor: colors.calcite,
          borderRight: '1px solid rgba(16, 22, 26, 0.15)',
          borderRadius: '3px',
        }}
      >
        <NotificationsList
          variant={'list'}
          messages={messages}
          selected={selected}
          onClickNotification={onClickNotification}
          viewProps={{
            leftBadges: [NotificationReadUnread],
            rightBadges: [NotificationArchive],
            headerBadges: [NotificationTags],
          }}
          onChangeMessage={onChange}
        />
      </Box>

      {selected && (
        <NotificationCard
          variant={'expanded'}
          message={selected}
          sx={{border: '1px solid rgba(16, 22, 26, 0.15)', borderRadius: '3px', margin: '30px auto'}}
          actionBadges={[NotificationReadUnread, NotificationArchive]}
          data-testid={`notification-msg-selected-${selected.id}`}
          onChangeMessage={onChange}
        />
      )}
    </KRow>
  );
};
